import { packageUpgradeJourney } from '@/Analytics/Segment';
import CreatePinPanel from '@/Components/VideoPlayer/ParentalControlPanel/CreatePinPanel';
import EnterPinPanel from '@/Components/VideoPlayer/ParentalControlPanel/EnterPinPanel';
import { LABELS_CONFIG, PACKAGE_UPGRADE_EVENTS, PKG_SUBSCRIBE_EVENT } from '@/Hooks/PUJ/constants';
import useAddCustomerPackage from '@/Hooks/PUJ/useAddCustomerPackage';
import useAuth from '@/Hooks/useAuth';
import { selectors } from '@/Store';
import getLabel from '@/Utils/LabelsConfig';
import redirectToLogin from '@/Utils/redirectToLogin';
import Icon from '@ant-design/icons';
import { ParentalControl } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { ReactComponent as Back } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_back.svg';
import { Button } from 'antd';
import Preloader from '@/Layouts/containers/Preloader';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import usePackageOfferDetails from '@/Hooks/PUJ/usePackageOfferDetails';
import PurchageSuccess from './PurchageSuccess';
import styles from './styles.module.scss';

export interface Props {
  offerId: string;
  packageName: string;
  packagePrice: string | number;
  collectionId: string;
  collectionTitle?: string;
  origin?: string;
  closeModal: () => void;
}

const PinValidation: FC<Props> = ({
  offerId,
  packageName,
  packagePrice: amount,
  collectionId,
  origin,
  closeModal,
}) => {
  const { addCustomerPackage, loading } = useAddCustomerPackage();
  const { getCollectionLink } = usePackageOfferDetails();

  const collectionIdRef = useRef<string | undefined>(collectionId);
  const parentalControl = useSelector(selectors.parentalPin.parentalControl);
  const [parentalResolved, setParentalResolved] = useState<boolean>(false);
  const [ispurchaseSuccess, setPurchaseSuccess] = useState<boolean>(false);

  const purchaseSuccess = callBackResponse => {
    if (callBackResponse === PKG_SUBSCRIBE_EVENT.ADD_CUSTOMER_PKG_SUCCESS_RESPONSE) {
      setPurchaseSuccess(true); // This will be set after the purchase completes successfully
    }
  };

  const onParentalResolved = async () => {
    // this will fetch the collectionId if not provided
    if (!collectionId && offerId) {
      collectionIdRef.current = await getCollectionLink(offerId);
    }
    addCustomerPackage({ offerId, packageName, packagePrice: amount }, origin, purchaseSuccess);
    setParentalResolved(true);
  };

  const closePinModal = () => {
    packageUpgradeJourney({
      action: PACKAGE_UPGRADE_EVENTS.PACKAGE_UPGRRADE_CANCELLED,
      origin,
      offer_id: offerId,
      offer_title: packageName,
      price: amount,
    });
    closeModal();
  };

  const getCreatePinDescription = (): JSX.Element => {
    return (
      <>
        ${`${amount}`} will reflect on your next bill. Please create your Parental Control PIN.
        <br />
        You can adjust it any time via My Account settings on <strong>sky.co.nz</strong>
      </>
    );
  };
  const auth = useAuth();
  useEffect(() => {
    if (!auth.isAuthenticated) {
      redirectToLogin();
    }
  }, [auth]);
  useEffect(() => {
    // If an offerId is present, the modal is triggered to start the PUJ journey.
    if (offerId) {
      packageUpgradeJourney({
        action: PACKAGE_UPGRADE_EVENTS.PACKAGE_UPGRRADE_INITIATED,
        origin,
        offer_id: offerId,
        offer_title: packageName,
        price: amount,
      });
    }
  }, [amount, offerId, packageName, origin]);
  return (
    <>
      {!ispurchaseSuccess && (
        <Button className={styles.backButton} title="fgh" type="link" onClick={closePinModal}>
          <Icon component={Back as React.FC} />
        </Button>
      )}
      <Preloader isLoading={loading}>
        {!loading && ispurchaseSuccess && (
          <PurchageSuccess
            origin={origin}
            packageName={packageName}
            collectionId={collectionIdRef.current}
          />
        )}
      </Preloader>
      {(!parentalResolved || !ispurchaseSuccess) &&
        (parentalControl === ParentalControl.NotSet ? (
          <CreatePinPanel
            onPinCreated={onParentalResolved}
            description={getCreatePinDescription()}
          />
        ) : (
          <EnterPinPanel
            onPinEntered={onParentalResolved}
            description={getLabel(LABELS_CONFIG.AGREEING_TERMS, { amount: `$${amount}` }, true)}
            customTitle={getLabel(LABELS_CONFIG.ENTER_PIN_TO_SUB, { packageName }, true)}
          />
        ))}
    </>
  );
};

export default PinValidation;
